import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import toast from 'react-hot-toast';
import { AlertTriangle, Clock, PackageMinus, PlayCircle } from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import {
  getEngineerPrioritiesByProjectId,
  getMaterialBlockedTasks,
  getOperationsDailyStats
} from '../../services/api/implementation/impl';
import AddRequirementAll from './components/AddRequirementAll';
import {
  applyMaterialBlock,
  blockedTasksAsShortItems,
  isMaterialShort,
  liveTaskIdFromMaterial,
  pickTodayBuckets,
  toRequirementSelectedTask
} from '../../utils/operationsToday';

const todayIso = () => moment().format('YYYY-MM-DD');

const TaskCard = ({ task, tone, onOpen, onRaiseShortage }) => (
  <div className={`rounded-xl border bg-white p-3 shadow-sm ${tone}`}>
    <button type="button" onClick={onOpen} className="w-full text-left">
      <p className="text-sm font-semibold text-gray-900">{task?.name || task?.liveTask?.name || 'Task'}</p>
      {task?.workType?.name ? (
        <span className="mt-1 inline-block rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
          {task.workType.name}
        </span>
      ) : null}
      {task?.material_blocked ? (
        <p className="mt-2 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
          Blocked on {task.material_block_item || 'material'}
        </p>
      ) : null}
    </button>
    <div className="mt-2 flex items-center gap-2">
      <button type="button" onClick={onOpen} className="text-xs font-semibold text-[#2A565E] hover:underline">
        Update
      </button>
      <span className="text-gray-300">·</span>
      <button
        type="button"
        onClick={onRaiseShortage}
        className="text-xs font-semibold text-amber-800 hover:underline">
        Raise shortage
      </button>
    </div>
  </div>
);

const OperationsToday = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const { onBack } = useSmartBack({ fallback: '/builder/operations' });
  const showHomeBack = shouldShowModuleHomeBack('operations', location);
  const { projects, projectsForDropdown } = useProjectsList();
  const [shortageTask, setShortageTask] = React.useState(null);

  const initialProjectId = Number(
    location.state?.projectID || localStorage.getItem('operations_today_project') || projects?.[0]?.id || 0
  );
  const [projectId, setProjectId] = React.useState(initialProjectId || '');

  React.useEffect(() => {
    if (!projectId && projects?.[0]?.id) setProjectId(projects[0].id);
  }, [projectId, projects]);

  React.useEffect(() => {
    if (projectId) localStorage.setItem('operations_today_project', String(projectId));
  }, [projectId]);

  const today = todayIso();

  const {
    data: dailyData,
    isFetching: loadingStats,
    isError,
    error
  } = useQuery({
    queryKey: ['operations-today', projectId, today],
    queryFn: async () => {
      try {
        return await getOperationsDailyStats(projectId, moment(today).add(1, 'days').format('YYYY-MM-DD'));
      } catch {
        return getOperationsDailyStats(projectId, today);
      }
    },
    enabled: Boolean(projectId),
    retry: 0
  });

  const { data: prioritiesRes, isFetching: loadingPriorities } = useQuery({
    queryKey: ['operations-today-daily-tasks', projectId, today],
    queryFn: () => getEngineerPrioritiesByProjectId(projectId, today),
    enabled: Boolean(projectId)
  });

  const { data: blockedTasks = [] } = useQuery({
    queryKey: ['operations-today-material-blocked', projectId],
    queryFn: () => getMaterialBlockedTasks(projectId),
    enabled: Boolean(projectId)
  });

  React.useEffect(() => {
    mixpanel.track('Operations Today Viewed', {
      user_id: userId,
      project_id: projectId || null
    });
  }, [userId, projectId]);

  const dailyRows = prioritiesRes?.data || [];
  const buckets = useMemo(
    () =>
      pickTodayBuckets({
        stats: dailyData,
        dailyRows,
        statsFailed: isError && !dailyData
      }),
    [dailyData, dailyRows, isError]
  );

  const inProgress = useMemo(
    () => applyMaterialBlock(buckets.inProgress, blockedTasks),
    [buckets.inProgress, blockedTasks]
  );
  const delayed = useMemo(
    () => applyMaterialBlock(buckets.delayed, blockedTasks),
    [buckets.delayed, blockedTasks]
  );
  const materialShort = useMemo(() => {
    const fromDaily = (dailyData?.dailyUsedMaterialBlock?.dailyRequirementItems || []).filter(
      isMaterialShort
    );
    const existingTaskIds = new Set(
      fromDaily.map((item) => Number(liveTaskIdFromMaterial(item))).filter(Boolean)
    );
    const fromFlag = blockedTasksAsShortItems(blockedTasks).filter(
      (item) => !existingTaskIds.has(Number(item.task_id))
    );
    return [...fromDaily, ...fromFlag];
  }, [dailyData, blockedTasks]);

  const openTaskUpdates = (task) => {
    navigate('/builder/operations/tasksUpdates', {
      state: {
        projectID: Number(projectId),
        taskId: task?.liveTask?.id || task?.id
      }
    });
  };

  const openShortageForTask = (task) => {
    const selected = toRequirementSelectedTask(task, dailyRows);
    if (!selected?.task_id) {
      toast.error('This task is missing a live-task id, so a requirement cannot be raised yet.');
      return;
    }
    setShortageTask(selected);
  };

  const openShortageForMaterial = (item) => {
    const liveId = liveTaskIdFromMaterial(item);
    const match =
      [...inProgress, ...delayed].find((task) => Number(task.id) === liveId) ||
      dailyRows.find((row) => Number(row.task_id) === liveId || Number(row.liveTask?.id) === liveId);

    if (match) {
      openShortageForTask(match);
      return;
    }

    if (delayed.length + inProgress.length === 1) {
      openShortageForTask(delayed[0] || inProgress[0]);
      return;
    }

    toast.error('Pick the delayed or in-progress task and tap Raise shortage.');
  };

  return (
    <Layout>
      <Loader isVisible={loadingStats || loadingPriorities} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: "Today's Work" }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="pt-[5.5rem] px-6 pb-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">What to do on site today</h2>
            <p className="text-sm text-gray-500">
              Update a task, or raise a shortage on that same task without leaving Today.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(event.target.value)}
              className="h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => navigate('/builder/operations/tasksUpdates', { state: { projectID: Number(projectId) } })}
              className="h-10 rounded-lg bg-[#2A565E] px-4 text-sm font-semibold text-white">
              Update tasks
            </button>
          </div>
        </div>

        {isError && buckets.source !== 'stats' ? (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Daily report stats were unavailable
            {error?.response?.data?.message ? ` (${error.response.data.message})` : ''}. Showing live
            tasks for today instead.
          </div>
        ) : null}

        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { label: 'In progress', value: inProgress.length, icon: PlayCircle },
            { label: 'Delayed', value: delayed.length, icon: AlertTriangle },
            { label: 'Material short', value: materialShort.length, icon: PackageMinus }
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4">
              <item.icon size={20} className="text-[#2A565E]" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{item.label}</p>
                <p className="text-2xl font-semibold text-[#1E4A53]">{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <section className="rounded-2xl bg-[#F4F7F8] p-3">
            <div className="mb-3 flex items-center gap-2 px-1">
              <Clock size={16} className="text-[#2A565E]" />
              <h3 className="text-sm font-semibold text-[#1E4A53]">In progress</h3>
            </div>
            <div className="flex flex-col gap-3">
              {inProgress.length === 0 ? (
                <p className="rounded-xl bg-white p-4 text-center text-xs text-gray-400">No live tasks in progress</p>
              ) : (
                inProgress.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    tone="border-gray-200"
                    onOpen={() => openTaskUpdates(task)}
                    onRaiseShortage={() => openShortageForTask(task)}
                  />
                ))
              )}
            </div>
          </section>

          <section className="rounded-2xl bg-[#F4F7F8] p-3">
            <div className="mb-3 flex items-center gap-2 px-1">
              <AlertTriangle size={16} className="text-red-600" />
              <h3 className="text-sm font-semibold text-[#1E4A53]">Delayed</h3>
            </div>
            <div className="flex flex-col gap-3">
              {delayed.length === 0 ? (
                <p className="rounded-xl bg-white p-4 text-center text-xs text-gray-400">No delayed tasks today</p>
              ) : (
                delayed.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    tone="border-red-100"
                    onOpen={() => openTaskUpdates(task)}
                    onRaiseShortage={() => openShortageForTask(task)}
                  />
                ))
              )}
            </div>
          </section>

          <section className="rounded-2xl bg-[#F4F7F8] p-3">
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <PackageMinus size={16} className="text-amber-700" />
                <h3 className="text-sm font-semibold text-[#1E4A53]">Material short</h3>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {materialShort.length === 0 ? (
                <p className="rounded-xl bg-white p-4 text-center text-xs text-gray-400">
                  No short materials on today’s requirements. Use Raise shortage on a task to create
                  one.
                </p>
              ) : (
                materialShort.map((item) => {
                  const dailyRequirementId = Number(
                    item.dailyRequirement_id || item.dailyRequirement?.id || 0
                  );
                  return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-amber-100 bg-white p-3 text-left shadow-sm">
                    <button
                      type="button"
                      onClick={() => openShortageForMaterial(item)}
                      className="w-full text-left">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.item?.item_name || item.itemName || 'Material'}
                      </p>
                      {item.material_blocked ? (
                        <p className="mt-1 text-xs text-amber-800">
                          {item.name ? `${item.name} · ` : ''}Blocked on {item.material_block_item || 'material'}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-gray-500">
                          Requested {item.quantity ?? 0} · Released {item.releasedQuantity ?? item.quantityReleasedOnDate ?? 0}
                        </p>
                      )}
                      <p className="mt-2 text-xs font-semibold text-amber-800">Raise shortage on this task</p>
                    </button>
                    {dailyRequirementId > 0 ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/builder/inventory/site-receipts', {
                            state: {
                              projectID: Number(projectId),
                              dailyRequirementId
                            }
                          })
                        }
                        className="mt-2 text-xs font-semibold text-[#2A565E] hover:underline">
                        Open site receipt →
                      </button>
                    ) : null}
                  </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>

      {shortageTask ? (
        <AddRequirementAll
          selectedTask={shortageTask}
          projectId={Number(projectId)}
          isOpen
          onClose={() => setShortageTask(null)}
          onSuccess={() => {
            setShortageTask(null);
            toast.success('Shortage raised for this task');
            queryClient.invalidateQueries({ queryKey: ['operations-today-material-blocked', projectId] });
            queryClient.invalidateQueries({ queryKey: ['operations-today', projectId] });
          }}
        />
      ) : null}
    </Layout>
  );
};

export default OperationsToday;
